"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

interface Project {
  id: number;
  name: string;
  slug: string;
  description: string;
  image: string;
}

export default function ProjectsSections({ initialData }: { initialData?: Project[] }) {
  const initialCount = 9;
  const loadMoreCount = 3;

  const [projects, setProjects] = useState<Project[]>(initialData || []);
  const [visibleCount, setVisibleCount] = useState(initialCount);
  const [loading, setLoading] = useState(!initialData);

useEffect(() => {
  if (projects.length > 0) return;

  const fetchProjects = async () => {
    try {
      const res = await fetch("https://portal.renvixx.com/api/projects");
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Received non-JSON response from server");
      }
      const json = await res.json();

      if (json.success) {
        // Sort projects ascending by id (or name)
        const sortedProjects = json.data.sort((a: Project, b: Project) => a.id - b.id);
        setProjects(sortedProjects);
      }
    } catch (error) {
      console.error("Failed to fetch projects", error);
    } finally {
      setLoading(false);
    }
  };

  fetchProjects();
}, [projects]);


  const handleSeeMore = () => {
    if (visibleCount < projects.length) {
      setVisibleCount((prev) => prev + loadMoreCount);
    } else {
      setVisibleCount(initialCount);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const isAllVisible = visibleCount >= projects.length;

  if (loading) {
    return (
      <section className="py-20 text-center">
        <p className="text-gray-500">Loading projects...</p>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white text-center">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Heading */}
        <p className="text-sm font-medium text-gray-600 mb-2">Projects</p>
        <h2 className="text-4xl sm:text-5xl font-extrabold text-gray-900">
          Innovation that <br />
          <span className="text-[#2a6380]">Endures.</span>
        </h2>

        {/* Projects Grid */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.slice(0, visibleCount).map((project) => (
            <div
              key={project.id}
              className="custom-cut bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:-translate-y-2"
            >
              <Link href={`/projects/${project.slug}`}>
                <div className="relative w-full rounded-lg h-60 cursor-pointer">
                  <div className="absolute inset-0 bg-black [clip-path:polygon(30px_0,100%_0,100%_100%,0_100%,0_30px)]"></div>
                  <Image
                    src={project.image}
                    alt={project.name}
                    fill
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, 33vw"
                  />
                </div>
              </Link>

              <div className="p-6 text-left rounded-b-lg border-l-2 border-r-2 border-b-2 border-[#33c1c761]">
                <h3 className="text-lg font-semibold text-gray-900">
                  {project.name}
                </h3>

                {/* description contains HTML → safe render */}
                <div
                  className="text-sm text-gray-600 mt-2 line-clamp-2"
                  dangerouslySetInnerHTML={{
                    __html: project.description,
                  }}
                />

                <Link
                  href={`/projects/${project.slug}`}
                  className="text-[#33c1c7] text-sm font-medium mt-4 inline-block hover:text-[#33c1c7]/80 transition-colors"
                >
                  Learn more &gt;
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* See More Button */}
        {projects.length > initialCount && (
          <div className="max-w-[140px] mx-auto mt-7">
            <button
              onClick={handleSeeMore}
              className="relative overflow-hidden inline-block w-full border-2 border-[#2a6380] bg-[#2a6380] text-white text-sm font-semibold px-8 py-3 rounded transition-all duration-500 group"
            >
              <span className="relative z-10 group-hover:text-[#2a6380]">
                {isAllVisible ? "Show Less" : "See More"}
              </span>
              <span className="absolute inset-0 bg-white -translate-x-full group-hover:translate-x-0 transition-transform duration-500"></span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
